import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';

// Universal parser for Minecraft text (handles raw JSON objects, JSON strings, and § color codes)
const formatMinecraftText = (raw) => {
  if (!raw) return '';

  const colorMap = {
    '0': '#000000', '1': '#0000AA', '2': '#00AA00', '3': '#00AAAA',
    '4': '#AA0000', '5': '#AA00AA', '6': '#FFAA00', '7': '#AAAAAA',
    '8': '#555555', '9': '#5555FF', 'a': '#55FF55', 'b': '#55FFFF',
    'c': '#FF5555', 'd': '#FF55FF', 'e': '#FFFF55', 'f': '#FFFFFF',
    black: '#000000', dark_blue: '#0000AA', dark_green: '#00AA00', dark_aqua: '#00AAAA',
    dark_red: '#AA0000', dark_purple: '#AA00AA', gold: '#FFAA00', gray: '#AAAAAA',
    dark_gray: '#555555', blue: '#5555FF', green: '#55FF55', aqua: '#55FFFF',
    red: '#FF5555', light_purple: '#FF55FF', yellow: '#FFFF55', white: '#FFFFFF'
  };

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      try {
        const parsed = JSON.parse(trimmed);
        return formatMinecraftText(parsed);
      } catch (e) {}
    }

    const parts = raw.split(/(§[0-9a-fk-or])/gi);
    let currentColor = '#FFFFFF';
    let isBold = false;
    let isItalic = false;

    return parts.map((part, idx) => {
      if (part.startsWith('§')) {
        const code = part.charAt(1).toLowerCase();
        if (colorMap[code]) {
          currentColor = colorMap[code];
        } else if (code === 'l') {
          isBold = true;
        } else if (code === 'o') {
          isItalic = true;
        } else if (code === 'r') {
          currentColor = '#FFFFFF';
          isBold = false;
          isItalic = false;
        }
        return null;
      }
      return (
        <span
          key={idx}
          style={{
            color: currentColor,
            fontWeight: isBold ? 'bold' : 'normal',
            fontStyle: isItalic ? 'italic' : 'normal'
          }}
        >
          {part}
        </span>
      );
    });
  }

  if (Array.isArray(raw)) {
    return raw.map((item, idx) => <React.Fragment key={idx}>{formatMinecraftText(item)}</React.Fragment>);
  }

  if (typeof raw === 'object' && raw !== null) {
    const color = colorMap[raw.color] || raw.color || '#FFFFFF';
    const bold = !!raw.bold;
    const italic = !!raw.italic;
    const underlined = !!raw.underlined;

    return (
      <span
        style={{
          color,
          fontWeight: bold ? 'bold' : 'normal',
          fontStyle: italic ? 'italic' : 'normal',
          textDecoration: underlined ? 'underline' : 'none'
        }}
      >
        {raw.text ? raw.text : ''}
        {raw.extra ? formatMinecraftText(raw.extra) : ''}
      </span>
    );
  }

  return <span>{String(raw)}</span>;
};

// Item icon and theme mapper based on item name/displayName
const getItemVisuals = (item) => {
  if (!item) return { icon: 'fa-cube', color: 'text-purple-400', border: 'border-purple-500/20' };

  const nameStr = `${item.name || ''} ${item.displayName || ''}`.toLowerCase();

  if (nameStr.includes('compass') || nameStr.includes('компас')) {
    return { icon: 'fa-compass', color: 'text-amber-400', border: 'border-amber-500/30' };
  }
  if (nameStr.includes('clock') || nameStr.includes('watch') || nameStr.includes('часы')) {
    return { icon: 'fa-clock', color: 'text-yellow-400', border: 'border-yellow-500/30' };
  }
  if (nameStr.includes('sword') || nameStr.includes('меч') || nameStr.includes('axe') || nameStr.includes('топор')) {
    return { icon: 'fa-shield-halved', color: 'text-rose-400', border: 'border-rose-500/30' };
  }
  if (nameStr.includes('portal') || nameStr.includes('ender') || nameStr.includes('портал') || nameStr.includes('глаз')) {
    return { icon: 'fa-circle-nodes', color: 'text-fuchsia-400', border: 'border-fuchsia-500/30' };
  }
  if (nameStr.includes('chest') || nameStr.includes('сундук') || nameStr.includes('box')) {
    return { icon: 'fa-vault', color: 'text-amber-300', border: 'border-amber-500/30' };
  }
  if (nameStr.includes('star') || nameStr.includes('звезд') || nameStr.includes('nether_star')) {
    return { icon: 'fa-star', color: 'text-cyan-300', border: 'border-cyan-500/30' };
  }
  if (nameStr.includes('diamond') || nameStr.includes('алмаз') || nameStr.includes('gem') || nameStr.includes('emerald') || nameStr.includes('изумруд')) {
    return { icon: 'fa-gem', color: 'text-emerald-400', border: 'border-emerald-500/30' };
  }
  if (nameStr.includes('book') || nameStr.includes('книга') || nameStr.includes('paper') || nameStr.includes('бумаг')) {
    return { icon: 'fa-book-bookmark', color: 'text-indigo-400', border: 'border-indigo-500/30' };
  }
  if (nameStr.includes('fire') || nameStr.includes('огонь') || nameStr.includes('blaze') || nameStr.includes('лава') || nameStr.includes('flame')) {
    return { icon: 'fa-fire-flame-curved', color: 'text-orange-400', border: 'border-orange-500/30' };
  }
  if (nameStr.includes('bed') || nameStr.includes('кровать') || nameStr.includes('спавн')) {
    return { icon: 'fa-bed', color: 'text-red-400', border: 'border-red-500/30' };
  }
  if (nameStr.includes('map') || nameStr.includes('карт')) {
    return { icon: 'fa-map', color: 'text-teal-400', border: 'border-teal-500/30' };
  }
  if (nameStr.includes('skull') || nameStr.includes('head') || nameStr.includes('голов')) {
    return { icon: 'fa-user-ninja', color: 'text-gray-200', border: 'border-gray-500/30' };
  }

  return { icon: 'fa-cube', color: 'text-purple-300', border: 'border-purple-500/20' };
};

const BotGuiModal = ({ guiData, onSlotClick, onClose }) => {
  const [hoveredSlot, setHoveredSlot] = useState(null);

  // Handle ESC key to close modal cleanly
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (onClose) onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!guiData || !guiData.slots) return null;

  const { title = 'Меню сервера', slots = [], windowId = 0 } = guiData;

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/80 backdrop-blur-md cursor-pointer"
      />

      {/* Modal Dialog Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.92, y: 15 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 15 }}
        transition={{ type: "spring", stiffness: 450, damping: 30 }}
        className="relative z-10 w-full max-w-[460px] max-h-[90vh] flex flex-col bg-[#0e0e18]/95 border border-purple-500/30 rounded-2xl p-3 sm:p-5 shadow-2xl shadow-purple-950/80 overflow-hidden select-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-2.5 mb-2.5 sm:pb-3 sm:mb-3 border-b border-purple-500/20">
          <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-purple-500/15 border border-purple-500/30 flex items-center justify-center text-purple-400 shrink-0">
              <i className="fas fa-compass text-amber-300 text-xs sm:text-sm"></i>
            </div>
            <div className="min-w-0">
              <h3 className="text-xs sm:text-base font-bold text-white font-mono flex items-center gap-1.5 sm:gap-2 truncate">
                <span className="truncate">{title}</span>
                <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-normal shrink-0">
                  #{windowId}
                </span>
              </h3>
              <p className="text-[10px] sm:text-[11px] text-gray-400 truncate">Выберите пункт или действие</p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-all flex items-center justify-center cursor-pointer shrink-0"
            title="Закрыть (ESC)"
          >
            <i className="fas fa-times text-xs"></i>
          </button>
        </div>

        {/* 9-Column Responsive Slot Grid */}
        <div className="grid grid-cols-9 gap-0.5 sm:gap-1.5 p-1 sm:p-2 rounded-xl bg-[#08080e] border border-white/5 shadow-inner">
          {slots.map((item, idx) => {
            const hasItem = item && (item.displayName || item.name);
            const isHovered = hoveredSlot === idx;
            const visuals = hasItem ? getItemVisuals(item) : null;

            return (
              <div
                key={idx}
                onMouseEnter={() => hasItem && setHoveredSlot(idx)}
                onMouseLeave={() => setHoveredSlot(null)}
                onClick={() => hasItem && onSlotClick(windowId, item.slot !== undefined ? item.slot : idx)}
                className={`relative w-full aspect-square rounded-lg border flex flex-col items-center justify-center transition-all ${
                  hasItem
                    ? `bg-[#161622] hover:bg-purple-950/50 ${visuals.border} hover:border-purple-400 hover:scale-105 active:scale-95 shadow-sm cursor-pointer`
                    : 'bg-[#0f0f18]/50 border-white/[0.03] opacity-30 cursor-default'
                }`}
              >
                {hasItem && (
                  <>
                    <i className={`fas ${visuals.icon} ${visuals.color} text-[11px] sm:text-sm drop-shadow`}></i>
                    {item.count > 1 && (
                      <span className="absolute bottom-0 right-0.5 text-[8px] sm:text-[9px] font-mono font-bold text-yellow-300 drop-shadow">
                        {item.count}
                      </span>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Hovered Item Details & Lore (Compact Preview) */}
        <div className="mt-3 min-h-[50px] max-h-[90px] p-2.5 rounded-xl bg-[#08080e] border border-purple-500/20 font-mono text-[11px] overflow-y-auto custom-scrollbar">
          {hoveredSlot !== null && slots[hoveredSlot] ? (
            <div className="space-y-1">
              <div className="flex items-center justify-between font-bold text-xs text-cyan-300">
                <span className="truncate">{formatMinecraftText(slots[hoveredSlot].displayName || slots[hoveredSlot].name)}</span>
                <span className="text-gray-500 text-[10px] shrink-0 ml-2">Слот #{slots[hoveredSlot].slot !== undefined ? slots[hoveredSlot].slot : hoveredSlot}</span>
              </div>
              {slots[hoveredSlot].lore && slots[hoveredSlot].lore.length > 0 && (
                <div className="text-gray-300 text-[10px] space-y-0.5 pt-1 border-t border-white/5 leading-tight">
                  {slots[hoveredSlot].lore.map((line, lIdx) => (
                    <div key={lIdx}>{formatMinecraftText(line)}</div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-gray-500 italic text-center flex items-center justify-center h-full text-[11px]">
              Наведите курсор на слот для просмотра описания
            </div>
          )}
        </div>
      </motion.div>
    </div>,
    document.body
  );
};

export default BotGuiModal;
